<script setup>
import { onMounted, ref } from "vue";
import { initializeApp } from "firebase/app";
import store from "@/store";
import api from "@/api";
import {
  getMessaging,
  getToken,
  onMessage,
  isSupported,
} from "firebase/messaging";

const STORAGE_KEY_NAME = "notificationPreference";
const isActive = ref(false);
const showBanner = ref(false);

const firebaseApp = initializeApp({
  apiKey: process.env.VUE_APP_FIREBASE_API_KEY,
  authDomain: process.env.VUE_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.VUE_APP_FIREBASE_PROJECT_ID,
  messagingSenderId: process.env.VUE_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.VUE_APP_FIREBASE_APP_ID,
});

const reject = () => {
  setPreference("denied");
};

const allow = async () => {
  const supported = await isSupported();

  if (!supported) {
    setPreference("unsupported");
    return;
  }

  let permission = await Notification.requestPermission();
  setPreference(permission);

  if (permission !== "granted") {
    console.warn("Notification permission denied");
    return;
  }

  try {
    const messaging = getMessaging(firebaseApp);

    let hasToken = localStorage.getItem("fcm_token");

    if (hasToken == null || !hasToken) {
      const token = await getToken(messaging, {
        vapidKey: process.env.VUE_APP_VAPID_KEY,
        serviceWorkerRegistration: await navigator.serviceWorker.ready,
      });

      localStorage.setItem("fcm_token", true);

      await api.post("/notifications/device/register", { token });
    }

    onMessage(messaging, (payload) => {
      const { title, body, icon } = payload.notification || {};

      const notificationTitle = title || "SellersMart — New Notification";
      const notificationOptions = {
        body: body || "You have a new message.",
        icon: icon || "./img/icons/android-launchericon-512-512.png",
      };

      if (Notification.permission === "granted") {
        new Notification(notificationTitle, notificationOptions);
      }
    });
  } catch (err) {
    console.error("Messaging error:", err);
  }
};

const setPreference = (value) => {
  localStorage.setItem(STORAGE_KEY_NAME, value);
  showBanner.value = false;
  isActive.value = false;
};

onMounted(() => {
  const pref = localStorage.getItem(STORAGE_KEY_NAME);
  if (!pref) {
    showBanner.value = true;
    store.state.notificationPreference = true;

    setTimeout(() => {
      isActive.value = true;
    }, 1500);
  } else {
    store.state.notificationPreference = false;
  }
});
</script>

<template>
  <div>
    <div class="notification-banner-backdrop" v-if="showBanner"></div>
    <div
      class="notification-banner"
      :class="{ active: isActive }"
      v-if="showBanner"
    >
      <h4>Enable Notifications</h4>
      <p class="py-2">
        Get real-time alerts for updates and important notices that matters.
      </p>
      <div
        class="d-flex align-items-center justify-content-end"
        style="gap: 0.5rem"
      >
        <button class="reject" @click="reject">Reject</button>
        <button class="accept" @click="allow">Allow</button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.notification-banner-backdrop {
  content: " ";
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: 100001;
}
.notification-banner {
  background-color: white;
  width: Min(582px, 95%);
  max-height: 60vh;
  height: auto;
  overflow: hidden;
  padding: 1rem 1.5rem;
  position: fixed;
  z-index: 100002;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  opacity: 0;
  transition: all 1s ease-in-out;
  border-radius: 15px;
}
.active {
  opacity: 1;
}
button {
  height: 38px;
  padding: 0 13px;
  gap: 8px;
  border-radius: 20px;
  outline: none;
  border: none;
  background-color: #f3f3f3;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  line-height: 16px;
  text-align: center;
  min-width: 91px;
}
.accept {
  background-color: #338b75;
  color: white;
}
.reject {
  background-color: #f1f1f1;
  color: #338b75;
}
</style>
